import { useEffect } from "react";
import { DatePicker, Notification, RadioButton } from "@rio-cloud/rio-uikit";
import { Occurrence } from "../../../../models/occurrence";
import { Scheduling } from "../../../../models/schedule"
import OccurrenceVehicleSummaryCard from "./components/occurrenceVehicleSummaryCard"
import { useState } from "react";
import { fetchMaintenanceStampData } from "../../../../api/fetchMaintenanceStampData";
import { useIntl } from "react-intl";
import { fetchUser } from "../../../../api/fetchUser";
import { getUserProfile } from "../../../../configuration/login/loginSlice";
import { useSelector } from "react-redux";

type CheckupType = "BREAK_IN" | "INTERMEDIATE" | "L" | "MP1" | "MP2";

interface IFormState {
  osNumber: string;
  dateEnd: string;
  hourMeter: string;
  odometer: string;
  dealership: string;
  consultant: string;
  checkupType: CheckupType;
}

interface IStepReleaseFormProps {
  scheduling: Scheduling | undefined;
  occurrence: Occurrence | null;
  closeFirstModal?: () => void
  setReleaseFormDialog: React.Dispatch<React.SetStateAction<boolean>>
}

const CHECKUP_TYPE_MAP: Record<number, CheckupType> = {
  1: "BREAK_IN",
  2: "INTERMEDIATE",
  3: "L",
  4: "MP1",
  5: "MP2",
};

const CHECKUP_OPTION_INDEX: Record<CheckupType, number> = {
  BREAK_IN: 1,
  INTERMEDIATE: 2,
  L: 3,
  MP1: 4,
  MP2: 5,
};

const StepReleaseForm = ({ 
  scheduling, 
  occurrence, 
  closeFirstModal, 
  setReleaseFormDialog 
}: IStepReleaseFormProps) => {
  const [formState, setFormState] = useState<IFormState>({
    osNumber: occurrence?.osNumber ?? "",
    dateEnd: occurrence?.osDtOpenAt ? new Date(occurrence.osDtOpenAt).toISOString() : "",
    hourMeter: occurrence?.vehicleHourmeter?.toString() ?? "",
    odometer: occurrence?.vehicleOdometer?.toString() ?? "",
    dealership: occurrence?.dealershipName ?? "",
    consultant: "",
    checkupType: "BREAK_IN",
  });

  const intl = useIntl();

  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (field: keyof IFormState) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormState(prev => ({ ...prev, [field]: event.target.value }));
  };

  const handleRadioChange = (optionIndex: number) => {
    const checkupType = CHECKUP_TYPE_MAP[optionIndex];
    setFormState(prev => ({ ...prev, checkupType }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setIsLoading(true);

    const body = {
      chassis: occurrence?.vehicleChassis,
      vehiclePlate: occurrence?.vehicleLicensePlate,
      vehicleName: occurrence?.vehicleName,
      vehicleModel: occurrence?.vehicleModel,
      vehicleYear: occurrence?.vehicleYear,
      emissionStandard: occurrence?.emissionStandard,
      
      osNumber: formState.osNumber,
      dateEnd: formState.dateEnd ? new Date(formState.dateEnd).toISOString() : "",
      hourMeter: Number(formState.hourMeter),
      odometer: Number(formState.odometer),
      dealership: formState.dealership,
      checkupType: formState.checkupType,
      consultant: formState.consultant,
      volksEssentialsPlanContract: occurrence?.volksEssentialsPlanContract
    };

    try {
      const stampResult = await fetchMaintenanceStampData(occurrence?.scheduleId || 'new', body);

      if (stampResult.status === 'rejected' || stampResult.value === null) {
        Notification.error(intl.formatMessage({ id: "intl-msg:occurrence.errorSavingData" }));
        return;
      }

      Notification.success(intl.formatMessage({ id: "intl-msg:occurrence.successSavingData" }));
      setReleaseFormDialog(false);
      closeFirstModal?.();
    } catch {
      Notification.error(intl.formatMessage({ id: "intl-msg:occurrence.unexpectedError" }));
    } finally {
      setIsLoading(false);
    }
  };

  const selectedOptionIndex = CHECKUP_OPTION_INDEX[formState.checkupType];

  const userProfile = useSelector(getUserProfile);
  useEffect(() => {
    const getUsername = async (): Promise<string> => {
      const userId = userProfile?.sub?.split(":")[1];
      if (!userId) {
        return "";
      }
      const user = await fetchUser(userId);
      return user?.name ?? "";
    };

    getUsername()
      .then(username => {
        setFormState(prev => ({ ...prev, consultant: username }));
      });
  }, []);

  return (
    <div>
      <OccurrenceVehicleSummaryCard
        scheduling={scheduling}
        occurrence={occurrence}
        allowMaintenanceGroupChange={false}
      />

      <div className="border-t-2 pt-4">
        <p className="text-[#2A3740] font-bold text-3xl">{intl.formatMessage({ id: "intl-msg:occurrence.digitalStamp" })}</p>
        <p>{intl.formatMessage({ id: "intl-msg:occurrence.digitalStampDescriptionInfo" })}</p>

        <form className="pt-7" onSubmit={handleSubmit}>
          <div className="grid gap-7 [grid-template-columns:repeat(auto-fit,minmax(220px,1fr))]">
            <div className="form-group margin-0">
              <label className="text-size-14 text-color-darker margin-bottom-10">
                {intl.formatMessage({ id: "intl-msg:occurrence.osNumber" })}:
              </label>
              <input
                type="text"
                className="form-control"
                placeholder=""
                value={formState.osNumber}
                onChange={handleChange("osNumber")}
              />
            </div>
            <div className="form-group margin-0">
              <label className="text-size-14 text-color-darker margin-bottom-10">
                {intl.formatMessage({ id: "intl-msg:occurrence.openDate" })}:
              </label>
              <DatePicker
                id="updateDateEnd"
                value={formState.dateEnd ? new Date(formState.dateEnd) : undefined}
                onChange={(e: any) => {
                  if (typeof e === 'string' && e.length) return;
                  if (typeof e === 'string') {
                    setFormState(prev => ({ ...prev, dateEnd: "" }));
                    return;
                  }
                  const newStartDate = e?.toISOString('YYYY-MM-DD')?.split('T')[0];
                  setFormState(prev => ({ ...prev, dateEnd: newStartDate }));
                }}
                locale="pt-BR"
                dateFormat="DD-MM-YYYY"
                timeFormat={false}
                mandatory={false}
                clearableInput={true}
                className="date-picker-large"
              />
            </div>
            <div className="form-group margin-0">
              <label className="text-size-14 text-color-darker margin-bottom-10">
                {intl.formatMessage({ id: "intl-msg:occurrence.hourMeter" })}:
              </label>
              <input
                type="number"
                className="form-control"
                placeholder=""
                value={formState.hourMeter}
                onChange={handleChange("hourMeter")}
              />
            </div>
            <div className="form-group margin-0">
              <label className="text-size-14 text-color-darker margin-bottom-10">
                {intl.formatMessage({ id: "intl-msg:occurrence.odometer" })}:
              </label>
              <input
                type="number"
                className="form-control"
                placeholder=""
                value={formState.odometer}
                onChange={handleChange("odometer")}
              />
            </div>
            <div className="form-group margin-0">
              <label className="text-size-14 text-color-darker margin-bottom-10">
                {intl.formatMessage({ id: "intl-msg:occurrence.dealership" })}:
              </label>
              <input
                type="text"
                className="form-control"
                placeholder=""
                value={formState.dealership}
                onChange={handleChange("dealership")}
              />
            </div>
            <div className="form-group margin-0">
              <label className="text-size-14 text-color-darker margin-bottom-10">
                {intl.formatMessage({ id: "intl-msg:occurrence.consultant" })}:
              </label>
              <input
                type="text"
                className="form-control"
                placeholder=""
                value={formState.consultant}
                onChange={handleChange("consultant")}
              />
            </div>
          </div>

          <div className="mt-14">
            <RadioButton
              name="radioGroup"
              onChange={() => handleRadioChange(1)}
              checked={selectedOptionIndex === 1}
              inline
            >
              {intl.formatMessage({ id: "intl-msg:occurrence.checkupType.breakIn" })}
            </RadioButton>

            <RadioButton
              name="radioGroup"
              onChange={() => handleRadioChange(2)}
              checked={selectedOptionIndex === 2}
              inline
            >
              {intl.formatMessage({ id: "intl-msg:occurrence.checkupType.intermediate" })}
            </RadioButton>

            <RadioButton
              name="radioGroup"
              onChange={() => handleRadioChange(3)}
              checked={selectedOptionIndex === 3}
              inline
            >
              {intl.formatMessage({ id: "intl-msg:occurrence.checkupType.lubrication" })}
            </RadioButton>

            <RadioButton
              name="radioGroup"
              onChange={() => handleRadioChange(4)}
              checked={selectedOptionIndex === 4}
              inline
            >
              {intl.formatMessage({ id: "intl-msg:occurrence.checkupType.mp1" })}
            </RadioButton>

            <RadioButton
              name="radioGroup"
              onChange={() => handleRadioChange(5)}
              checked={selectedOptionIndex === 5}
              inline
            >
              {intl.formatMessage({ id: "intl-msg:occurrence.checkupType.mp2" })}
            </RadioButton>
          </div>

          <div className="mt-4 pt-9 border-t-2" />

          <div className="text-gray-500 mb-5 w-full text-left">*{intl.formatMessage({ id: "intl-msg:occurrence.digitalStampDescription" })}</div>

          <div className="display-flex justify-content-end mt-5">
            <button
              type="submit"
              className="btn btn-primary w-[257px]"
              disabled={isLoading}
            >
              {isLoading ? intl.formatMessage({ id: "intl-msg:occurrence.saving" }) : intl.formatMessage({ id: "intl-msg:occurrence.save" })}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StepReleaseForm;